import { map, STEM_UNLOCK, STEM_LEVEL } from '../data/shared/song';
import { SONG_PLAYBACK_MODE } from '../data/songPlayback';
import { GAME_CONFIG } from '../data/gameConfig';

export class StemPlayer {
  constructor(mode = SONG_PLAYBACK_MODE) {
    this.mode = mode;
    this.buffers = new Map();
    this.voices = new Map();
    this.generation = 0;
  }

  async load() {
    const fullMix = this.mode === 'full';
    const tracks = fullMix ? { full: { file: 'full.mp3' } } : map.tracks;
    if (this.buffers.size === Object.keys(tracks).length) return;
    const generation = ++this.generation;
    this.controller?.abort();
    const controller = new AbortController();
    this.controller = controller;
    const timeout = setTimeout(() => controller.abort(), 60000);
    try {
      const OfflineContext = window.OfflineAudioContext || window.webkitOfflineAudioContext;
      if (!OfflineContext) throw new Error('El navegador no puede preparar las pistas de audio.');
      const decoder = new OfflineContext(fullMix ? 2 : 1, 1, fullMix ? 44100 : 22050);
      const buffers = new Map();
      for (const [name, track] of Object.entries(tracks)) {
        const response = await fetch(`${process.env.PUBLIC_URL || ''}/music/nacanaca/${track.file}`, { signal: controller.signal });
        if (!response.ok) throw new Error(`No se pudo cargar la pista ${name}.`);
        const buffer = await decoder.decodeAudioData(await response.arrayBuffer());
        if (generation !== this.generation || controller.signal.aborted) throw new Error('Carga de audio cancelada.');
        if (Math.abs(buffer.duration - map.duration) > 0.15) throw new Error(`La pista ${name} no coincide con el mapa de ritmo.`);
        buffers.set(name, buffer);
      }
      this.buffers = buffers;
    } finally { clearTimeout(timeout); }
  }

  start(ctx, bus, origin, speed, songTime = -GAME_CONFIG.rhythm.songLeadSeconds / speed) {
    this.stop();
    const lead = GAME_CONFIG.rhythm.songLeadSeconds;
    const when = Math.max(ctx.currentTime, origin - lead / speed);
    const elapsed = Math.max(0, songTime);
    const sourceOffset = Math.max(0, songTime * speed + lead) % map.duration;
    for (const [name, buffer] of this.buffers) {
      const source = ctx.createBufferSource();
      const gain = ctx.createGain();
      source.buffer = buffer;
      source.playbackRate.value = speed;
      source.loop = true;
      source.loopEnd = map.duration;
      gain.gain.value = name === 'full' ? 0.75 : elapsed >= STEM_UNLOCK[name] ? STEM_LEVEL[name] : 0;
      if (name !== 'full' && elapsed < STEM_UNLOCK[name]) {
        const unlock = origin + STEM_UNLOCK[name];
        gain.gain.setValueAtTime(0, unlock);
        gain.gain.linearRampToValueAtTime(STEM_LEVEL[name], unlock + 0.3);
      }
      source.connect(gain);
      gain.connect(bus);
      source.start(when, sourceOffset);
      this.voices.set(name, { source, gain });
    }
  }

  accent(name, when, hit, now) {
    const voice = this.voices.get(name);
    if (!voice) return;
    const start = Math.max(when, now);
    const level = STEM_LEVEL[name];
    // Reinforce the already playing instrument, preserving its actual attack.
    voice.gain.gain.cancelScheduledValues(start);
    voice.gain.gain.setTargetAtTime(level * (hit ? 1.5 : 0.55), start, 0.008);
    voice.gain.gain.setTargetAtTime(level, start + 0.12, 0.06);
  }

  stop() {
    this.voices.forEach(({ source, gain }) => {
      try { source.stop(); } catch (_) { /* already stopped */ }
      source.disconnect(); gain.disconnect();
    });
    this.voices.clear();
  }

  dispose() {
    this.generation++;
    this.controller?.abort();
    this.stop();
    this.buffers.clear();
  }
}
